import { useState, useEffect, useCallback } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  Globe,
  Loader2,
  Lock,
  Plus,
  Shield,
  Sparkles,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';

import { Avatar, Badge, Button, SearchInput } from '../../../components/ui';
import { CreateClubModal } from '../../../components/CreateClubModal';
import { trpc } from '../../../lib/trpc';

export const Route = createFileRoute('/app/onboarding/step-3')({
  component: FindCommunityStep,
});

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function FindCommunityStep() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [search, setSearch] = useState('');
  const [createClubOpen, setCreateClubOpen] = useState(false);
  const debouncedSearch = useDebounce(search, 300);

  const { data, isLoading, isFetching } = trpc.club.list.useQuery({
    search: debouncedSearch || undefined,
    limit: 9,
    sort: 'most_members',
  });

  const joinMutation = trpc.clubMember.join.useMutation({
    onError: (error) => {
      if (error.data?.code === 'CONFLICT') {
        toast.error('You are already a member of this club');
      } else if (error.data?.code === 'FORBIDDEN') {
        toast.error('This club is not accepting new members');
      } else {
        toast.error('Failed to join club. Please try again.');
      }
    },
  });

  const completeMutation = trpc.user.completeOnboarding.useMutation({
    onSuccess: () => {
      utils.user.me.invalidate();
    },
  });

  const handleJoinAndComplete = useCallback(
    async (clubId: string, slug: string) => {
      try {
        await joinMutation.mutateAsync({ clubId });
        await completeMutation.mutateAsync();
        navigate({ to: '/app/clubs/$slug', params: { slug } });
      } catch {
        // Errors handled by individual mutation onError callbacks
      }
    },
    [joinMutation, completeMutation, navigate],
  );

  const handleSkip = async () => {
    try {
      await completeMutation.mutateAsync();
      navigate({ to: '/app' });
    } catch {
      toast.error('Something went wrong. Please try again.');
    }
  };

  const clubs = data?.items ?? [];
  const isPending = joinMutation.isPending || completeMutation.isPending;

  return (
    <div className="animate-fade-up p-4 md:p-6">
      <div className="relative rounded-2xl bg-surface-900/80 border border-surface-700/50 overflow-hidden">
        {/* Top accent */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-500/40 to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(38,217,181,0.04)_0%,_transparent_60%)] pointer-events-none" />

        <div className="relative px-6 sm:px-8 py-8 space-y-6">
          {/* Header */}
          <div className="text-center space-y-2">
            <h1 className="text-2xl sm:text-3xl font-display font-bold tracking-tight">
              Find Your Community
            </h1>
            <p className="text-sm text-surface-400">
              Join a club to start competing
            </p>
          </div>

          {/* Search */}
          <SearchInput
            placeholder="Search clubs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={() => setSearch('')}
            loading={isFetching && !!search}
          />

          {/* Club list */}
          <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1 scrollbar-thin">
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl bg-surface-800/40 animate-pulse"
                >
                  <div className="size-10 rounded-lg bg-surface-700/60" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-4 w-32 rounded bg-surface-700/50" />
                    <div className="h-3 w-48 rounded bg-surface-700/30" />
                  </div>
                  <div className="h-8 w-16 rounded bg-surface-700/40" />
                </div>
              ))
            ) : clubs.length === 0 ? (
              <div className="py-10 text-center">
                <Users className="size-8 text-surface-600 mx-auto mb-3" />
                <p className="text-sm text-surface-400">
                  {search ? 'No clubs match your search' : 'No clubs available yet'}
                </p>
              </div>
            ) : (
              clubs.map((club) => (
                <ClubRow
                  key={club.id}
                  club={club}
                  isPending={isPending}
                  joiningId={
                    joinMutation.isPending && joinMutation.variables != null && 'clubId' in joinMutation.variables
                      ? joinMutation.variables.clubId
                      : undefined
                  }
                  onJoin={handleJoinAndComplete}
                />
              ))
            )}
          </div>

          {/* Create club CTA */}
          <div className="text-center pt-2">
            <button
              type="button"
              onClick={() => setCreateClubOpen(true)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-surface-400 hover:text-brand-400 transition-colors"
            >
              <Plus className="size-3.5" />
              Or create your own club
            </button>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2">
            <Button
              variant="ghost"
              onClick={() => navigate({ to: '/app/onboarding/step-2' })}
              leftIcon={<ArrowLeft className="size-4" />}
              disabled={isPending}
            >
              Back
            </Button>
            <Button
              variant="ghost"
              onClick={handleSkip}
              disabled={isPending}
              leftIcon={completeMutation.isPending && !joinMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : undefined}
            >
              {completeMutation.isPending && !joinMutation.isPending ? 'Finishing...' : 'Skip for now'}
            </Button>
          </div>
        </div>
      </div>

      <CreateClubModal open={createClubOpen} onClose={() => setCreateClubOpen(false)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Club Row
// ---------------------------------------------------------------------------

const privacyConfig = {
  public: { icon: Globe, label: 'Public', color: 'green' as const },
  private: { icon: Lock, label: 'Private', color: 'yellow' as const },
  invite_only: { icon: Shield, label: 'Invite Only', color: 'purple' as const },
};

type ClubItem = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  avatarUrl: string | null;
  privacy: 'public' | 'private' | 'invite_only';
  memberCount: number;
};

function ClubRow({
  club,
  isPending,
  joiningId,
  onJoin,
}: {
  club: ClubItem;
  isPending: boolean;
  joiningId?: string;
  onJoin: (clubId: string, slug: string) => void;
}) {
  const privacy = privacyConfig[club.privacy];
  const isPublic = club.privacy === 'public';
  const isJoiningThis = joiningId === club.id;

  return (
    <div
      className={`
        flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-200
        border-surface-700/30 bg-surface-800/30 hover:border-surface-600/60 hover:bg-surface-800/60
      `}
    >
      <Avatar
        src={club.avatarUrl}
        alt={club.name}
        initials={club.name.charAt(0)}
        size="md"
        variant="rounded"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-sm text-white truncate">{club.name}</p>
          {club.privacy !== 'public' && (
            <Badge variant="subtle" color={privacy.color} size="sm">
              {privacy.label}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3 mt-0.5">
          <span className="text-xs text-surface-500 flex items-center gap-1">
            <Users className="size-3" />
            {club.memberCount}
          </span>
          {club.description && (
            <span className="text-xs text-surface-500 truncate">
              {club.description}
            </span>
          )}
        </div>
      </div>
      {isPublic ? (
        <Button
          variant="primary"
          size="sm"
          onClick={() => onJoin(club.id, club.slug)}
          disabled={isPending}
          leftIcon={isJoiningThis ? <Loader2 className="size-3.5 animate-spin" /> : undefined}
        >
          {isJoiningThis ? 'Joining...' : 'Join'}
        </Button>
      ) : (
        <span className="text-xs text-surface-500 shrink-0">
          {privacy.label}
        </span>
      )}
    </div>
  );
}
